import type { DealFloatingRateLoanData } from '@src/types/OTC';
import { isFloatingRateLoanSecurity, LoanQuoteTerminationTypeEnum, logger, useSecuritiesContext } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { InspectorText } from './InspectorText';
import { LoanAmountDisplay } from './LoanAmountDisplay';
import { buildLoanScheduleDealInformationFields } from './shared/buildLoanScheduleDealInformationFields';
import { InspectorSectionsRenderer } from './shared/InspectorSectionsRenderer';
import type { DisplayOutputItem } from './shared/types';
import { useCounterpartyInformation } from './shared/useCounterpartyInformation';
import { useTimingInformation } from './shared/useTimingInformation';
import { SpreadDisplay } from './SpreadDisplay';

const useFloatingRateLoanTradeInformation = (instrumentData: DealFloatingRateLoanData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isFloatingRateLoanSecurity(dealSecurity) ? dealSecurity : undefined;
  if (!resolvedDealSecurity) {
    logger.error(
      new Error(`Failed to get floating-rate loan deal security for symbol ${instrumentData.Symbol ?? '(missing)'}`),
      {
        extra: {
          dealSecurity,
          resolvedDealSecurity,
        },
      }
    );
    return {
      sectionTitle: 'Deal Information',
      fields: [],
    };
  }

  const rateSource = instrumentData.DealInfo.RateSource || resolvedDealSecurity.ProductDetails.RateSource || '-';
  const termType = resolvedDealSecurity.ProductDetails.TermType;
  const termTypeLabel = termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Open' : 'Fixed';

  return {
    sectionTitle: 'Deal Information',
    fields: compact([
      {
        fieldName: 'Term Type',
        fieldValue: <InspectorText>{termTypeLabel}</InspectorText>,
      },
      {
        fieldName: termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Initial Term' : 'Term',
        fieldValue: (
          <InspectorText>
            {termType === LoanQuoteTerminationTypeEnum.OpenTerm
              ? resolvedDealSecurity.ProductDetails.InitialFixedTerm
              : resolvedDealSecurity.ProductDetails.Term}
          </InspectorText>
        ),
      },
      {
        fieldName: 'Side',
        fieldValue: <InspectorText>{instrumentData.DealInfo.Side}</InspectorText>,
      },
      {
        fieldName: 'Loan Amount',
        fieldValue: <LoanAmountDisplay instrumentData={instrumentData} dealSecurity={resolvedDealSecurity} />,
      },
      {
        fieldName: 'Rate Source',
        fieldValue: <InspectorText>{rateSource ?? '-'}</InspectorText>,
      },
      {
        fieldName: 'Spread',
        fieldValue: <SpreadDisplay instrumentData={instrumentData} />,
      },
      ...buildLoanScheduleDealInformationFields(resolvedDealSecurity.ProductDetails),
    ]),
  };
};

interface FloatingRateLoanInstrumentInspectorSectionsProps {
  instrumentData: DealFloatingRateLoanData;
}

export const FloatingRateLoanInstrumentInspectorSections = ({
  instrumentData,
}: FloatingRateLoanInstrumentInspectorSectionsProps) => {
  const tradeInfo = useFloatingRateLoanTradeInformation(instrumentData);
  const counterpartyInfo = useCounterpartyInformation(instrumentData);
  const timingInfo = useTimingInformation(instrumentData);

  const sections = [tradeInfo, counterpartyInfo, timingInfo];

  return <InspectorSectionsRenderer sections={sections} />;
};
